import { useCallback } from "react";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";
import { CommentPopover } from "../ui/CommentPopover";
import { AiCommentIndicator } from "@/components/ai/AiCommentIndicator";
import type { CommentCellProps } from "./types";
import {
  AiChangeBadge,
  AiM3CommentsBadge,
  AI_PENDING_CELL_CLASS,
  isAiPendingField,
  isAiPendingFlowM3Comments,
} from "./AiChangeBadge";
import { cn } from "@/lib/utils";

export function CommentCell({
  flow,
  disabled,
  onFlowUpdate,
}: CommentCellProps) {
  const aiPendingComment = isAiPendingField(flow, "comment");
  // Pending m3_comments are staged inside the `flowMetadata` blob field
  // alongside other metadata keys (nb_colis, ai, …). We surface ONLY
  // m3_comments to the user — the others are either internal markers or
  // owned by their own dedicated cells.
  const aiPendingM3 = isAiPendingFlowM3Comments(flow);
  const aiPending = aiPendingComment || aiPendingM3;
  const exportMode = useFeatureFlag("export_mode") as string | undefined;
  const isVivoMode = exportMode === "vivo";

  const handleCommentChange = useCallback(
    (value: string) => {
      onFlowUpdate(flow.id, { comment: value || null });
    },
    [onFlowUpdate, flow.id],
  );

  const handleHiddenCommentChange = useCallback(
    (value: string) => {
      onFlowUpdate(flow.id, { hidden_comment: value || null });
    },
    [onFlowUpdate, flow.id],
  );

  const handleM3CommentsChange = useCallback(
    (m3Comments: {
      c1: string;
      c2: string;
      c3: string;
      c4: string;
      c5: string;
      qualite: string;
    }) => {
      onFlowUpdate(flow.id, {
        flow_metadata: {
          ...flow.flow_metadata,
          m3_comments: m3Comments,
        },
      });
    },
    [onFlowUpdate, flow.id, flow.flow_metadata],
  );

  return (
    <div
      // Stable handle for the guidance overlay's "add a comment" step (the
      // whole comment cell — the popover trigger lives inside CommentPopover).
      data-testid="flow-comment-cell"
      className={cn(
        "flex items-center gap-0.5",
        aiPending && AI_PENDING_CELL_CLASS,
      )}
    >
      <AiCommentIndicator
        comments={flow.flow_metadata?.ai_comments ?? []}
        entityType="pre_order_flow"
        entityId={flow.id}
        size="sm"
      />
      <div className="flex-1 min-w-0">
        <CommentPopover
          value={flow.comment}
          hiddenValue={flow.hidden_comment}
          onChange={handleCommentChange}
          onHiddenChange={handleHiddenCommentChange}
          disabled={disabled}
          flowMetadata={flow.flow_metadata}
          onM3CommentsChange={handleM3CommentsChange}
          isVivoMode={isVivoMode}
        />
        <AiChangeBadge flow={flow} field="comment" />
        <AiM3CommentsBadge flow={flow} />
      </div>
    </div>
  );
}
